import React, { useState, useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useTypedSelector } from "../app/store"
import { useCreateTeamSimulationCheckoutMutation } from "../app/services/meseeknaApiV2"
import HeaderSection from "../components/header-section"
import Sidebar from "../features/sidebar/components/sidebar"

// Check environment for payment bypass
// Payment bypass is DISABLED by default and only enabled in:
// 1. Development mode (NODE_ENV === "development")
// 2. Staging environment (hostname includes "staging")
// 3. Explicit opt-in via REACT_APP_BYPASS_TEAM_SIM_PAYMENT=true
const isStaging = typeof window !== "undefined" && window.location.hostname.includes("staging")
const isDevelopment = process.env.NODE_ENV === "development"
const isExplicitBypass = process.env.REACT_APP_BYPASS_TEAM_SIM_PAYMENT === "true"

const BYPASS_PAYMENT = isDevelopment || isStaging || isExplicitBypass

const TeamSimulationPurchase: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const organization = useTypedSelector((state) => state.authSlice.organization)
  const user = useTypedSelector((state) => state.authSlice.user)

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [createCheckout] = useCreateTeamSimulationCheckoutMutation()

  // Check for payment cancellation
  useEffect(() => {
    if (searchParams.get("payment") === "cancelled") {
      setError("Payment was cancelled. You can try again when ready.")
    }
  }, [searchParams])

  const handlePurchase = async () => {
    // Bypass payment in dev/staging - go directly to setup
    if (BYPASS_PAYMENT) {
      navigate("/admin/team-simulations/setup")
      return
    }

    if (!organization?.id) {
      setError("Organization not found. Please refresh and try again.")
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const result = await createCheckout({
        scenarioId: 0, // Will be selected in setup
        name: "", // Will be set in setup
        participantEmails: [], // Will be set in setup
        organizationId: Number(organization.id),
        createdBy: user?.id,
        successUrl: `${window.location.origin}/admin/team-simulations/setup?payment=success`,
        cancelUrl: `${window.location.origin}/admin/team-simulations/purchase?payment=cancelled`,
      }).unwrap()

      // Redirect to Stripe checkout
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl
      } else {
        setError("Failed to create checkout session. Please try again.")
      }
    } catch (err: unknown) {
      console.error("Checkout error:", err)
      const errorMessage = err instanceof Error ? err.message : "Failed to initiate payment. Please try again."
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="relative bg-base-white w-full h-[100dvh] flex flex-row items-start justify-start font-text-md-regular">
      <Sidebar />
      <div className="w-full h-[100dvh] flex-1 flex flex-col">
        <HeaderSection />

        <div className="flex-1 overflow-y-auto">
          <div className="min-h-full bg-gray-50 py-12 px-6">
            <div className="max-w-3xl mx-auto">
              {/* Back button */}
              <button
                onClick={() => navigate("/dashboard/recruiter-admin/team-simulations")}
                className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
                Back to Team Simulations
              </button>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-[#faaa32] to-[#e09a2a] px-8 py-10 text-white">
                  <div className="flex items-center mb-4">
                    <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mr-4">
                      <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div>
                      <h1 className="text-3xl font-bold">Team Dynamics Simulation</h1>
                      <p className="text-white/80 mt-1">Collaborative assessment for up to 10 team members</p>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-8">
                  <div className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">What you&apos;ll discover</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-start">
                        <div className="w-8 h-8 bg-[#faaa32]/10 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                          <svg className="w-4 h-4 text-[#faaa32]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">Information Flow</h3>
                          <p className="text-sm text-gray-600">How information flows between team members</p>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <div className="w-8 h-8 bg-[#faaa32]/10 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                          <svg className="w-4 h-4 text-[#faaa32]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">Friction Points</h3>
                          <p className="text-sm text-gray-600">Where friction or misalignment is likely to emerge</p>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <div className="w-8 h-8 bg-[#faaa32]/10 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                          <svg className="w-4 h-4 text-[#faaa32]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">Decision Dynamics</h3>
                          <p className="text-sm text-gray-600">Who naturally drives decisions vs. who holds back</p>
                        </div>
                      </div>

                      <div className="flex items-start">
                        <div className="w-8 h-8 bg-[#faaa32]/10 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                          <svg className="w-4 h-4 text-[#faaa32]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">Style Interactions</h3>
                          <p className="text-sm text-gray-600">How different cognitive styles interact under pressure</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6 mb-8">
                    <h3 className="font-medium text-gray-900 mb-2">Ideal for:</h3>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Launching a new initiative or project</li>
                      <li>• Restructuring or reorganizing a team</li>
                      <li>• Integrating new members into an existing group</li>
                      <li>• Preparing for high-stakes collaborative work</li>
                    </ul>
                  </div>

                  {/* Pricing */}
                  <div className="border border-gray-200 rounded-lg p-6 mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">$199</h3>
                        <p className="text-gray-600">One-time payment</p>
                      </div>
                      <div className="text-right text-sm text-gray-600">
                        <p>Up to 10 team members</p>
                        <p>~30 minute simulation</p>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-4 mb-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Includes:</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li className="flex items-center">
                          <svg className="w-4 h-4 text-[#50d16b] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          Collaborative crisis simulation scenario
                        </li>
                        <li className="flex items-center">
                          <svg className="w-4 h-4 text-[#50d16b] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          Team dynamics analysis report
                        </li>
                        <li className="flex items-center">
                          <svg className="w-4 h-4 text-[#50d16b] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          Individual contribution insights
                        </li>
                        <li className="flex items-center">
                          <svg className="w-4 h-4 text-[#50d16b] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          Calendar invites for all participants
                        </li>
                      </ul>
                    </div>
                  </div>

                  {error && (
                    <div className="bg-[#ff5050]/10 border border-[#ff5050]/30 rounded-lg p-4 mb-6">
                      <p className="text-[#ff5050] text-sm">{error}</p>
                    </div>
                  )}

                  {BYPASS_PAYMENT && (
                    <div className="bg-[#ffca4b]/20 border border-[#ffca4b]/50 rounded-lg p-4 mb-6">
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-[#faaa32] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <p className="text-[#3d5669] text-sm font-medium">
                          {isDevelopment ? "Development" : "Staging"} Mode: Payment will be bypassed
                        </p>
                      </div>
                    </div>
                  )}

                  <button
                    onClick={handlePurchase}
                    disabled={isLoading}
                    className="w-full px-6 py-3 bg-[#faaa32] text-white font-medium rounded-lg hover:bg-[#e09a2a] transition-colors disabled:bg-[#faaa32]/50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </>
                    ) : BYPASS_PAYMENT ? (
                      `Continue to Setup (${isDevelopment ? "Dev" : "Staging"} Mode)`
                    ) : (
                      "Purchase & Continue"
                    )}
                  </button>

                  <p className="text-center text-gray-500 text-sm mt-4">
                    {BYPASS_PAYMENT
                      ? `Payment bypassed in ${isDevelopment ? "development" : "staging"} mode`
                      : "Secure payment powered by Stripe"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TeamSimulationPurchase
